import { useCallback, useEffect, useState } from "react";
import PageContainer from "../components/layout/PageContainer";
import { formatUSD } from "../utils/money";
import { getRevenueReport, downloadBookingsCsv } from "../api/reports";

const GROUP_OPTIONS = [
  { value: "month", label: "Month" },
  { value: "service", label: "Service" },
  { value: "customer", label: "Customer" },
  { value: "status", label: "Status" },
];

function monthsAgo(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function todayIso() {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.toISOString().slice(0, 10);
}

export default function AdminReports() {
  const [start, setStart] = useState(monthsAgo(3));
  const [end, setEnd] = useState(todayIso());
  const [groupBy, setGroupBy] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getRevenueReport({ start, end, groupBy });
      setData(result);
    } catch (e) {
      setError(e.message || "Could not load report.");
    } finally {
      setLoading(false);
    }
  }, [start, end, groupBy]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCsv() {
    setDownloading(true);
    try {
      await downloadBookingsCsv({ start, end });
    } catch (e) {
      setError(e.message || "Could not export CSV.");
    } finally {
      setDownloading(false);
    }
  }

  const totals = data?.totals || {};
  const breakdown = data?.breakdown || [];
  const maxRevenue = breakdown.reduce((m, r) => Math.max(m, Number(r.revenue_cents) || 0), 0);

  return (
    <>
      <PageContainer>
        <div className="bb-container py-6 sm:py-8">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl text-birdie-green tracking-wide">
                Reports
              </h1>
              <p className="text-birdie-charcoal/70 font-body mt-1">
                Revenue and booking activity at a glance.
              </p>
            </div>
            <button
              type="button"
              onClick={handleCsv}
              disabled={downloading}
              className="bb-btn-mustard"
            >
              {downloading ? "Preparing..." : "Download CSV"}
            </button>
          </div>

          <div className="bb-card p-4 sm:p-6 mb-6 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            <label className="flex flex-col text-sm font-body font-semibold text-birdie-charcoal">
              From
              <input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="bb-input mt-1"
              />
            </label>
            <label className="flex flex-col text-sm font-body font-semibold text-birdie-charcoal">
              To
              <input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="bb-input mt-1"
              />
            </label>
            <label className="flex flex-col text-sm font-body font-semibold text-birdie-charcoal col-span-2 sm:col-span-1">
              Group by
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="bb-input mt-1"
              >
                {GROUP_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 lg:gap-4 mb-6">
            <StatCard label="Revenue" value={formatUSD(totals.revenue_cents || 0)} tone="green" />
            <StatCard label="Tax collected" value={formatUSD(totals.tax_collected_cents || 0)} tone="charcoal" />
            <StatCard label="Pipeline" value={formatUSD(totals.pipeline_cents || 0)} tone="mustard" />
            <StatCard label="Booked" value={(totals.booked_count || 0).toString()} tone="charcoal" />
            <StatCard label="Canceled" value={(totals.canceled_count || 0).toString()} tone="warm" />
          </div>

          <div className="bb-card p-4 sm:p-6">
            <h2 className="font-accent text-xl text-birdie-charcoal mb-4">
              Breakdown by {GROUP_OPTIONS.find((o) => o.value === groupBy)?.label.toLowerCase()}
            </h2>

            {loading && !data ? (
              <div className="py-10 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-birdie-green" />
              </div>
            ) : breakdown.length === 0 ? (
              <p className="py-8 text-center text-birdie-charcoal/70 font-body">
                No bookings in this range.
              </p>
            ) : (
              <div className="space-y-2">
                {breakdown.map((row) => {
                  const revenue = Number(row.revenue_cents) || 0;
                  const pct = maxRevenue > 0 ? (revenue / maxRevenue) * 100 : 0;
                  return (
                    <div
                      key={row.label}
                      className="border border-birdie-green/10 rounded-xl p-3 sm:p-4"
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <div className="font-body font-semibold text-birdie-charcoal">
                          {row.label || "(unlabeled)"}
                        </div>
                        <div className="flex items-center gap-4 text-sm font-body">
                          <span className="text-birdie-charcoal/70">
                            {row.booking_count} bookings
                          </span>
                          <span className="font-semibold text-birdie-green">
                            {formatUSD(revenue)}
                          </span>
                          {Number(row.pipeline_cents) > 0 && (
                            <span className="text-birdie-mustard-dark">
                              + {formatUSD(row.pipeline_cents)} pipeline
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-full h-2 bg-birdie-beige rounded-full overflow-hidden">
                        <div
                          className="h-full bg-birdie-green"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </PageContainer>
    </>
  );
}

function StatCard({ label, value, tone }) {
  const toneClass = {
    green: "border-birdie-green/20 bg-birdie-green/5 text-birdie-green",
    mustard: "border-birdie-mustard/30 bg-birdie-mustard/10 text-birdie-mustard-dark",
    charcoal: "border-birdie-charcoal/20 bg-white text-birdie-charcoal",
    warm: "border-birdie-warmgray/30 bg-birdie-beige text-birdie-charcoal",
  }[tone] || "";

  return (
    <div className={`rounded-xl border p-3 sm:p-4 ${toneClass}`}>
      <div className="text-[10px] sm:text-xs font-body font-semibold uppercase tracking-wide opacity-80 truncate">
        {label}
      </div>
      <div className="font-accent text-lg sm:text-2xl lg:text-3xl mt-1 break-words">{value}</div>
    </div>
  );
}
